import { useRef, useEffect } from "react";
import { C, STEPS } from "./utils/tokens";

import GlobalStyle from "./components/GlobalStyle";
import { Orb, Grid, Btn } from "./components/primitives";
import Nav from "./components/Nav";
import Stepper from "./components/Stepper";
import Landing from "./components/Landing";
import TemplatePicker from "./components/TemplatePicker";
import StepNavBar from "./components/StepNavBar";
import AnimatedBackground from "./components/AnimatedBackground";
import { useLocalStorage, clearProgress } from "./hooks/Uselocalstorage";

import Step1 from "./steps/Step1";
import Step2 from "./steps/Step2";
import Step3 from "./steps/Step3";
import Step4 from "./steps/Step4";
import Step7 from "./steps/Step7";

const DEFAULT_INFO = {
  nome: "",
  cognome: "",
  ruolo: "",
  email: "",
  telefono: "",
  citta: "",
  linkedin: "",
  sommario: "",
  foto: null,
};
const DEFAULT_ESP = [
  { id: 1, azienda: "", ruolo: "", periodo: "", descrizione: "" },
];
const DEFAULT_FORM = [{ id: 1, istituto: "", titolo: "", anno: "", voto: "" }];

export default function App() {
  const mousePos = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const handle = (e) => {
      mousePos.current = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      };
    };
    window.addEventListener("mousemove", handle);
    return () => window.removeEventListener("mousemove", handle);
  }, []);

  const [page, setPage] = useLocalStorage("cvcraft_page", "landing");
  const [step, setStep] = useLocalStorage("cvcraft_step", 1);
  const [tpl, setTpl] = useLocalStorage("cvcraft_tpl", null);
  const [paid, setPaid] = useLocalStorage("cvcraft_paid", false);
  const [info, setInfo] = useLocalStorage("cvcraft_info", DEFAULT_INFO);
  const [esperienze, setEsp] = useLocalStorage("cvcraft_esp", DEFAULT_ESP);
  const [formazione, setForm] = useLocalStorage("cvcraft_form", DEFAULT_FORM);
  const [competenze, setComp] = useLocalStorage("cvcraft_comp", []);

  const cvData = { info, esperienze, formazione, competenze };
  const go = (s) => {
    setStep(s);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const hasProgress = info.nome || info.cognome || esperienze[0]?.azienda;

  const resetAll = () => {
    clearProgress();
    setPage("landing");
    setStep(1);
    setTpl(null);
    setPaid(false);
    setInfo(DEFAULT_INFO);
    setEsp(DEFAULT_ESP);
    setForm(DEFAULT_FORM);
    setComp([]);
  };

  return (
    <>
      <GlobalStyle />

      <style>{`
        /* ── INIEZIONE CSS CIRCOSCRITTA ESCLUSIVAMENTE AL BANNER RECOVERY ── */
        .cvcraft-recovery-wrapper {
          position: fixed;
          bottom: 24px;
          left: 0;
          width: 100%;
          display: flex;
          justify-content: center;
          padding: 0 16px;
          box-sizing: border-box;
          z-index: 9999;
          pointer-events: none; /* Non blocca i click sullo sfondo */
        }

        .cvcraft-recovery-banner {
          pointer-events: auto; /* Ripristina i click sui bottoni del banner */
          background: linear-gradient(135deg, #1a1b00, #0e0f0a);
          border: 1px solid ${C.goldBorder};
          border-radius: 16px;
          padding: 16px 24px;
          display: flex;
          align-items: center;
          gap: 20px;
          box-shadow: 0 8px 40px rgba(245, 197, 24, 0.25);
          animation: fadeUp 0.4s ease both;
          width: 100%;
          max-width: 560px;
          box-sizing: border-box;
        }

        .cvcraft-recovery-banner .inner-layout {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 1;
        }

        .cvcraft-recovery-banner .actions-layout {
          display: flex;
          gap: 8px;
        }

        @media (max-width: 768px) {
          .cvcraft-recovery-wrapper {
            bottom: 16px;
          }
          
          .cvcraft-recovery-banner {
            flex-direction: column !important;
            text-align: center !important;
            padding: 16px !important;
            gap: 14px !important;
            max-width: 420px !important;
          }

          .cvcraft-recovery-banner .inner-layout {
            flex-direction: column !important;
            gap: 8px !important;
          }

          .cvcraft-recovery-banner .actions-layout {
            width: 100% !important;
            justify-content: center !important;
          }

          .cvcraft-recovery-banner .actions-layout button {
            flex: 1 !important;
            justify-content: center !important;
          }
        }
      `}</style>

      {/* Animated background — builder E picker */}
      {(page === "builder" || page === "picker") && (
        <AnimatedBackground
          step={page === "picker" ? 0 : step}
          mousePos={mousePos}
        />
      )}

      {/* Orbs solo su landing */}
      {page === "landing" && (
        <>
          <Orb
            x={-5}
            y={5}
            s={700}
            c="radial-gradient(circle,rgba(245,197,24,1),transparent)"
          />
          <Orb
            x={108}
            y={55}
            s={500}
            c="radial-gradient(circle,rgba(255,90,30,1),transparent)"
          />
          <Orb
            x={50}
            y={88}
            s={380}
            c="radial-gradient(circle,rgba(100,100,255,0.9),transparent)"
          />
          <Grid />
        </>
      )}

      <Nav
        page={page}
        step={step}
        tpl={tpl}
        onLogo={() => {
          setPage("landing");
          setStep(1);
        }}
      />

      <main
        style={{
          paddingTop: 58,
          minHeight: "100vh",
          position: "relative",
          zIndex: 1,
          width: "100%",
        }}
      >
        {/* Banner salvataggio — Struttura a Wrapper infallibile per la centratura */}
        {page === "landing" && hasProgress && (
          <div className="cvcraft-recovery-wrapper">
            <div className="cvcraft-recovery-banner">
              {/* Contenitore Icona + Testi */}
              <div className="inner-layout">
                <span style={{ fontSize: 22, lineHeight: 1 }}>💾</span>
                <div style={{ width: "100%" }}>
                  <div
                    style={{
                      fontFamily: "'Syne',sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      color: C.text,
                      marginBottom: 2,
                    }}
                  >
                    Hai un CV salvato
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: C.muted,
                      wordBreak: "break-word",
                    }}
                  >
                    {info.nome ? `${info.nome} ${info.cognome} — ` : ""}Continua
                    da dove hai lasciato.
                  </div>
                </div>
              </div>

              {/* Pulsanti d'Azione */}
              <div className="actions-layout">
                <Btn
                  size="sm"
                  onClick={() => setPage(tpl ? "builder" : "picker")}
                >
                  Continua →
                </Btn>
                <Btn size="sm" variant="secondary" onClick={resetAll}>
                  Ricomincia
                </Btn>
              </div>
            </div>
          </div>
        )}

        {page === "landing" && <Landing onStart={() => setPage("picker")} />}

        {page === "picker" && (
          <TemplatePicker
            onSelect={(t) => {
              setTpl(t);
              setPage("builder");
              setStep(1);
            }}
          />
        )}

        {page === "builder" && (
          <div
            style={{
              maxWidth: 780,
              margin: "0 auto",
              padding: "40px 20px 120px",
              position: "relative",
              zIndex: 1,
            }}
          >
            {/* Stepper con sfondo semitrasparente */}
            <div
              style={{
                background: "rgba(8,9,9,0.6)",
                backdropFilter: "blur(16px)",
                borderRadius: 16,
                padding: "16px 20px",
                border: "1px solid rgba(255,255,255,0.06)",
                marginBottom: 28,
              }}
            >
              <Stepper current={step} tpl={tpl} />
            </div>

            {/* Step content con card semitrasparente */}
            <div
              style={{
                background: "rgba(8,9,9,0.55)",
                backdropFilter: "blur(20px)",
                borderRadius: 20,
                padding: "32px 28px",
                border: "1px solid rgba(255,255,255,0.06)",
                boxShadow: "0 8px 48px rgba(0,0,0,0.4)",
              }}
            >
              {step === 1 && <Step1 d={info} s={setInfo} />}
              {step === 2 && <Step2 d={esperienze} s={setEsp} />}
              {step === 3 && <Step3 d={formazione} s={setForm} />}
              {step === 4 && <Step4 d={competenze} s={setComp} tpl={tpl} />}
              {step === 5 && (
                <Step7
                  cvData={cvData}
                  ai={{}}
                  cl=""
                  tpl={tpl}
                  paid={paid}
                  setPaid={setPaid}
                />
              )}
            </div>

            <StepNavBar
              step={step}
              tpl={tpl}
              info={info}
              esperienze={esperienze}
              formazione={formazione}
              onBack={() => (step === 1 ? setPage("picker") : go(step - 1))}
              onNext={() => go(step + 1)}
            />
          </div>
        )}
      </main>
    </>
  );
}
